import type { ResourceStoreContext, RouterContext, RouterDataContext } from '@townsquare/router/routing-types';

import { orgIdToAri } from '@townsquare/ari-utils';
import { createPlatformSiteAri } from '@townsquare/collab-context/create-site-ari';
import { createRelayResource, RelayResourceConfig } from '@townsquare/relay-utils';
import { getAggRelayEnvironment } from '@townsquare/relay-utils/agg-environment';
import type { HomeResourceContext } from '@townsquare/router/types';
import { fg } from '@townsquare/stat-sig/gate';

import SettingsViewQuery_TeamsSettingsQuery, {
	type SettingsViewQuery_TeamsSettingsQuery as SettingsViewQuery_TeamsSettingsQueryType,
} from '../components/__generated__/SettingsViewQuery_TeamsSettingsQuery.graphql';
import { getScreenFromStringOrDefault } from '../config/ScreenConfig';

const getTeamsSettingsResourceQuery = (
	routerContext: RouterContext | RouterDataContext,
	resourceContext: ResourceStoreContext,
): RelayResourceConfig<SettingsViewQuery_TeamsSettingsQueryType> => {
	const context = resourceContext as HomeResourceContext;
	const screen = getScreenFromStringOrDefault(routerContext.query.screen);

	const isTeamTypeEnabled = fg('enable_atlassian_team_types_m1');
	const isCustomFieldsEnabled = fg('enable_atlassian_team_custom_fields_m1');
	const isPermissionsEnabled = fg('ptc-enable-team-type-permission-enabled');

	// Determine which screens are active
	const isTeamTypesScreen = screen === 'TEAM_TYPES';
	const isTeamTypeEditScreen = screen === 'TEAM_TYPES_EDIT';
	const isNotificationsScreen = screen === 'NOTIFICATIONS';
	const isCustomFieldsScreen = screen === 'CUSTOM_FIELDS';

	// Extract teamTypeId from query params for edit operations
	const teamTypeId =
		isTeamTypeEditScreen && typeof routerContext.query.teamTypeId === 'string' ? routerContext.query.teamTypeId : '';

	// Load data conditionally based on screen and feature gate
	const shouldLoadTeamTypes = isTeamTypeEnabled && isTeamTypesScreen;
	const shouldLoadTeamTypeEdit = isTeamTypeEnabled && !!teamTypeId;
	const shouldLoadCustomFields = isCustomFieldsEnabled && isCustomFieldsScreen;
	const shouldLoadNotifications = isNotificationsScreen;

	const siteId = context.collabContext?.rovoWorkspace || context.workspace?.cloudId || '';
	const organizationId = context.workspace?.organisationId ?? context.orgId ?? '';

	const scopeId = createPlatformSiteAri(siteId ?? '') ?? '';

	return {
		environment: getAggRelayEnvironment(),
		query: SettingsViewQuery_TeamsSettingsQuery,
		variables: {
			scopeId,
			shouldLoadTeamTypes: shouldLoadTeamTypes,
			shouldLoadTeamTypeEdit,
			shouldLoadNotifications,
			shouldLoadCustomFields,
			teamTypeId,
			orgId: orgIdToAri(organizationId),
			siteId,
			permissionsEnabled: isPermissionsEnabled,
		},
	};
};

export const teamsSettingsResource = createRelayResource<SettingsViewQuery_TeamsSettingsQueryType>({
	getQuery: getTeamsSettingsResourceQuery,
});
